import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Heart, Star, Calendar, MapPin, Quote, CheckCircle2, Play,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import Reveal from "../components/Reveal";
import { heroImages, eventFlyer, eventVideo, paymentMethods, stats, programs, events, testimonials, partners, org } from "../mock";

const fmtDate = (d) =>
  new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });

const Home = () => {
  return (
    <div>
      {/* HERO */}
      <section className="relative pt-28 lg:pt-36 pb-20 overflow-hidden">
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-accent/10 blur-3xl" />
        <div className="absolute -bottom-32 -left-24 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="container-prc grid lg:grid-cols-2 gap-12 items-center relative">
          <div>
            <Badge className="bg-secondary text-secondary-foreground hover:bg-secondary rounded-full px-4 py-1.5 font-semibold mb-6">
              <Star className="h-3.5 w-3.5 mr-1.5 fill-accent text-accent" /> Trusted by 3,500+ local families
            </Badge>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-foreground text-balance">
              Empowering leaders, <span className="text-primary">elevating quality</span>, advancing community impact
            </h1>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-xl">
              Dallas Early Childhood Resource Institute (DECRI) is a community-based 501(c)(3) nonprofit founded in 2001, with a primary emphasis on supporting early childhood professionals and single parents.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button asChild className="btn-cta bg-accent hover:bg-accent/90 text-accent-foreground font-bold rounded-full px-7 h-12 text-base">
                <Link to="/donate"><Heart className="h-5 w-5 mr-2" /> Donate Today</Link>
              </Button>
              <Button asChild variant="outline" className="btn-cta border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground font-bold rounded-full px-7 h-12 text-base">
                <Link to="/programs">Explore Programs <ArrowRight className="h-5 w-5 ml-2" /></Link>
              </Button>
            </div>
            <div className="mt-8 flex items-center gap-5 text-sm text-muted-foreground">
              <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> 501(c)(3) nonprofit</span>
              <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> Sliding-scale pricing</span>
            </div>
          </div>

          <Reveal className="relative">
            <div className="rounded-[2rem] overflow-hidden shadow-lift bg-white" style={{ aspectRatio: "1024 / 885" }}>
              <img src={eventFlyer} alt="ECLD Network Event — Save the Date, October 24, 2026" className="w-full block" />
            </div>
            <Link to="/register" className="btn-cta absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-lift p-4 flex items-center gap-3 max-w-[250px] hover:bg-secondary/40 transition-colors">
              <span className="grid place-items-center h-12 w-12 rounded-xl bg-accent/10 text-accent shrink-0">
                <Calendar className="h-6 w-6" />
              </span>
              <div>
                <p className="font-display font-extrabold text-foreground leading-none">Register · $49</p>
                <p className="text-xs text-muted-foreground mt-1">Oct 24, 2026 · ECLD Network Event</p>
              </div>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* WAYS TO PAY */}
      <section className="py-16">
        <div className="container-prc">
          <Reveal className="text-center max-w-2xl mx-auto mb-8">
            <Badge className="bg-secondary text-secondary-foreground hover:bg-secondary rounded-full px-4 py-1.5 font-semibold mb-4">Ways to Give</Badge>
            <h2 className="font-display font-extrabold text-3xl lg:text-4xl text-foreground">Support our mission in seconds</h2>
            <p className="mt-4 text-muted-foreground">Scan a code below to give securely with Zelle, Venmo, or Cash App. Every gift invests in early childhood leaders and brighter tomorrows.</p>
          </Reveal>
          <div className="grid sm:grid-cols-3 gap-6">
            {paymentMethods.map((m, i) => (
              <Reveal key={m.name} delay={i * 90}>
                <div className="card-hover bg-white rounded-2xl p-6 shadow-soft text-center h-full flex flex-col items-center">
                  <div className="rounded-xl overflow-hidden bg-white border border-border w-full max-w-[220px]">
                    <img src={m.qr} alt={`${m.name} QR code — scan to give`} className="w-full block" />
                  </div>
                  <h3 className="font-display font-bold text-lg text-foreground mt-5">{m.name}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{m.note}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* UPCOMING EVENT VIDEO */}
      <section className="py-16 bg-secondary/40">
        <div className="container-prc">
          <Reveal className="grid lg:grid-cols-2 items-stretch bg-primary rounded-[2rem] overflow-hidden shadow-lift">
            <div className="relative bg-black min-h-[240px]">
              <video
                src={eventVideo}
                poster={heroImages.event}
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                controls
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
            <div className="p-8 lg:p-12 text-primary-foreground">
              <Badge className="bg-accent text-accent-foreground hover:bg-accent rounded-full font-semibold mb-4">Upcoming Event · Oct 24, 2026</Badge>
              <h2 className="font-display font-extrabold text-3xl lg:text-4xl leading-tight">ECLD Network Event 2026</h2>
              <p className="mt-4 text-primary-foreground/85 leading-relaxed">
                Where early childhood leaders connect, grow, and lead forward. Join us Saturday, October 24, 2026 in Dallas for a day of leadership, collaboration, and community.
              </p>
              <ul className="mt-5 grid grid-cols-2 gap-2.5 text-sm">
                {["Build Partnerships", "Share Ideas", "Strengthen Leadership", "Support Families"].map((h) => (
                  <li key={h} className="flex items-center gap-2 text-primary-foreground/90"><CheckCircle2 className="h-4 w-4 text-accent shrink-0" /> {h}</li>
                ))}
              </ul>
              <div className="mt-7 flex flex-wrap gap-4">
                <Button asChild className="btn-cta bg-accent hover:bg-accent/90 text-accent-foreground font-bold rounded-full px-7 h-12">
                  <Link to="/events">Save the Date <ArrowRight className="h-5 w-5 ml-2" /></Link>
                </Button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-primary text-primary-foreground">
        <div className="container-prc py-12 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 80} className="text-center">
              <p className="font-display font-extrabold text-3xl lg:text-4xl">{s.value}</p>
              <p className="text-sm text-primary-foreground/80 mt-1">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* MISSION */}
      <section className="py-20">
        <div className="container-prc grid lg:grid-cols-2 gap-12 items-center">
          <Reveal className="grid grid-cols-2 gap-4">
            <img src={heroImages.about} alt="Children playing" className="rounded-2xl object-cover h-56 w-full shadow-soft mt-8" />
            <img src={heroImages.aboutSecondary} alt="Family enrichment" className="rounded-2xl object-cover h-56 w-full shadow-soft" />
          </Reveal>
          <Reveal delay={100}>
            <Badge className="bg-secondary text-secondary-foreground hover:bg-secondary rounded-full px-4 py-1.5 font-semibold mb-5">Our Mission</Badge>
            <h2 className="font-display font-extrabold text-3xl lg:text-4xl text-foreground leading-tight">
              A welcoming home for every family’s first steps
            </h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              We believe strong communities begin with supported families. From a baby’s first playgroup to a caregiver’s first friendship, we’re here for the moments that matter most.
            </p>
            <ul className="mt-6 space-y-3">
              {["Affordable, inclusive programs for all", "Experienced, compassionate educators", "A vibrant, connected parent community"].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <span className="text-foreground/90">{t}</span>
                </li>
              ))}
            </ul>
            <Button asChild className="btn-cta mt-8 bg-primary hover:bg-primary/90 rounded-full px-7 h-12 font-bold">
              <Link to="/about">Learn Our Story <ArrowRight className="h-5 w-5 ml-2" /></Link>
            </Button>
          </Reveal>
        </div>
      </section>

      {/* PROGRAMS */}
      <section className="py-20 bg-secondary/40">
        <div className="container-prc">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <Badge className="bg-white text-primary hover:bg-white rounded-full px-4 py-1.5 font-semibold mb-4">What We Offer</Badge>
            <h2 className="font-display font-extrabold text-3xl lg:text-4xl text-foreground">Programs for every age & stage</h2>
            <p className="mt-4 text-muted-foreground">Enrichment, play, and support designed with love for children from birth to age six—and their caregivers.</p>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {programs.slice(0, 6).map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal key={p.id} delay={(i % 3) * 80}>
                  <div className="card-hover bg-white rounded-2xl overflow-hidden shadow-soft h-full flex flex-col">
                    <div className="img-zoom h-44">
                      <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex items-center justify-between mb-3">
                        <span className={`grid place-items-center h-11 w-11 rounded-xl ${p.color}`}><Icon className="h-5 w-5" /></span>
                        <Badge variant="outline" className="rounded-full font-semibold">{p.age}</Badge>
                      </div>
                      <h3 className="font-display font-bold text-lg text-foreground">{p.title}</h3>
                      <p className="mt-2 text-sm text-muted-foreground flex-1">{p.short}</p>
                      <Link to="/programs" className="mt-4 inline-flex items-center gap-1.5 text-primary font-bold text-sm hover:gap-2.5 transition-all">
                        Learn more <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* EVENTS */}
      <section className="py-20">
        <div className="container-prc">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
            <Reveal>
              <Badge className="bg-secondary text-secondary-foreground hover:bg-secondary rounded-full px-4 py-1.5 font-semibold mb-4">Mark Your Calendar</Badge>
              <h2 className="font-display font-extrabold text-3xl lg:text-4xl text-foreground">Upcoming events</h2>
            </Reveal>
            <Button asChild variant="outline" className="btn-cta border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-full font-bold">
              <Link to="/events">View all events <ArrowRight className="h-4 w-4 ml-2" /></Link>
            </Button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {events.map((ev, i) => (
              <Reveal key={ev.id} delay={(i % 4) * 70}>
                <Link to="/events" className="card-hover block bg-white rounded-2xl overflow-hidden shadow-soft h-full">
                  <div className="img-zoom h-36 relative">
                    <img src={ev.image} alt={ev.title} className="w-full h-full object-cover" />
                    <span className="absolute top-3 left-3 bg-white rounded-xl px-3 py-1.5 text-center shadow">
                      <span className="block font-display font-extrabold text-primary text-sm leading-none">{fmtDate(ev.date)}</span>
                    </span>
                  </div>
                  <div className="p-5">
                    <Badge className="bg-accent/10 text-accent hover:bg-accent/10 rounded-full font-semibold mb-2">{ev.category}</Badge>
                    <h3 className="font-display font-bold text-foreground leading-snug">{ev.title}</h3>
                    <p className="mt-2 text-xs text-muted-foreground flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {ev.location}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* VOLUNTEER CTA */}
      <section className="py-20 bg-secondary/40">
        <div className="container-prc">
          <Reveal className="relative rounded-[2rem] overflow-hidden shadow-lift">
            <img src={heroImages.volunteer} alt="Volunteers" className="absolute inset-0 w-full h-full object-cover" />
            <div className="hero-overlay absolute inset-0" />
            <div className="relative p-10 lg:p-16 max-w-2xl text-white">
              <h2 className="font-display font-extrabold text-3xl lg:text-4xl leading-tight">Your time changes lives</h2>
              <p className="mt-4 text-white/90 leading-relaxed">
                Join our community of 120+ volunteers helping in classrooms, events, and behind the scenes. Every hour makes a difference.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Button asChild className="btn-cta bg-accent hover:bg-accent/90 text-accent-foreground font-bold rounded-full px-7 h-12">
                  <Link to="/get-involved">Become a Volunteer</Link>
                </Button>
                <Button asChild variant="outline" className="btn-cta bg-white/10 border-2 border-white text-white hover:bg-white hover:text-primary font-bold rounded-full px-7 h-12">
                  <Link to="/about"><Play className="h-4 w-4 mr-2" /> Our Impact</Link>
                </Button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20">
        <div className="container-prc">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <Badge className="bg-secondary text-secondary-foreground hover:bg-secondary rounded-full px-4 py-1.5 font-semibold mb-4">Family Voices</Badge>
            <h2 className="font-display font-extrabold text-3xl lg:text-4xl text-foreground">Loved by our community</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <Reveal key={t.name} delay={i * 90}>
                <div className="card-hover bg-white rounded-2xl p-7 shadow-soft h-full flex flex-col">
                  <Quote className="h-8 w-8 text-accent/40" />
                  <p className="mt-4 text-foreground/90 leading-relaxed flex-1">“{t.quote}”</p>
                  <div className="mt-6 flex items-center gap-3">
                    <span className="grid place-items-center h-11 w-11 rounded-full bg-primary/10 text-primary font-display font-bold">{t.name[0]}</span>
                    <div>
                      <p className="font-display font-bold text-foreground leading-none">{t.name}</p>
                      <p className="text-xs text-muted-foreground mt-1">{t.role}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERS MARQUEE */}
      <section className="py-16 bg-secondary/40 overflow-hidden">
        <div className="container-prc text-center mb-8">
          <p className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Proudly supported by</p>
        </div>
        <div className="relative flex">
          <div className="flex gap-4 animate-marquee whitespace-nowrap">
            {[...partners, ...partners].map((p, i) => (
              <span key={`${p}-${i}`} className="px-7 py-3 bg-white rounded-full shadow-soft font-display font-bold text-primary/70">{p}</span>
            ))}
          </div>
        </div>
      </section>

      {/* DONATE CTA */}
      <section className="py-20">
        <div className="container-prc">
          <Reveal className="bg-primary rounded-[2rem] p-10 lg:p-16 text-center text-primary-foreground relative overflow-hidden">
            <div className="absolute -top-16 -right-10 h-56 w-56 rounded-full bg-white/10 blur-2xl" />
            <div className="absolute -bottom-16 -left-10 h-56 w-56 rounded-full bg-accent/20 blur-2xl" />
            <div className="relative max-w-2xl mx-auto">
              <Heart className="h-10 w-10 mx-auto text-accent" />
              <h2 className="font-display font-extrabold text-3xl lg:text-4xl mt-5">Help us keep every family connected</h2>
              <p className="mt-4 text-primary-foreground/85">Your gift funds scholarships, classes, and support circles that no family should have to go without.</p>
              <Button asChild className="btn-cta mt-8 bg-accent hover:bg-accent/90 text-accent-foreground font-bold rounded-full px-8 h-12 text-base">
                <Link to="/donate"><Heart className="h-5 w-5 mr-2" /> Make a Donation</Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
